import { FormModalData } from "@/components/common/FormModal";

/**
 * Converts form data from FormModal format to the API request format
 * Separates fields into serverCredentials and settings based on their source schema
 */
export function convertFormDataToApiFormat(
  values: FormModalData[],
  credentialsSchema: any,
  settingsSchema?: any
): { serverCredentials: Record<string, any>; settings: Record<string, any> } {
  const serverCredentials: Record<string, any> = {};
  const settings: Record<string, any> = {};

  // Add const fields from credentials schema
  if (credentialsSchema?.properties) {
    Object.entries(credentialsSchema.properties).forEach(([key, value]: [string, any]) => {
      if (value.const) {
        serverCredentials[key] = value.const;
      }
    });
  }

  // Separate form values by their source schema
  values.forEach((field) => {
    const fieldName = (field as any).fieldName;
    const value = convertValueByType(field.value, field.type);
    
    // Check if this field belongs to credentials or settings based on the schemas
    if (credentialsSchema?.properties?.[fieldName]) {
      serverCredentials[fieldName] = value;
    } else if (settingsSchema?.properties?.[fieldName]) {
      settings[fieldName] = value;
    }
  });

  return { serverCredentials, settings };
}

/**
 * Converts a value to its proper type based on the field type
 */
export function convertValueByType(value: string, type: string): any {
  if (type === "number") {
    // Treat blank/whitespace input as null so optional numeric fields
    // (e.g. HawkSoft officeId) aren't coerced to 0 by Number("").
    if (value.trim() === "") {
      return null;
    }
    return Number(value);
  }
  if (type === "boolean") {
    return value === "true" || value === "1";
  }
  // For email, url, tel, password, text - return as string
  return value;
}

/**
 * Determines if an integration is connected based on the connected integrations list
 */
export function isIntegrationConnected(
  integrationName: string,
  connectedIntegrations: any[]
): boolean {
  return connectedIntegrations.some(
    (connected) => connected.name === integrationName
  );
}

/**
 * Merges available integrations with connection status
 */
export function mergeIntegrationsWithStatus(
  availableIntegrations: any[],
  connectedIntegrations: any[]
): any[] {
  return availableIntegrations.map((integration) => ({
    ...integration,
    serverCredentials: connectedIntegrations.find((c) => c.name === integration.name)?.credentials || null,
    settings: connectedIntegrations.find((c) => c.name === integration.name)?.settings || null,
    id: connectedIntegrations.find((c) => c.name === integration.name)?.id,
    status: isIntegrationConnected(integration.name, connectedIntegrations)
      ? "connected"
      : "disconnected",
  }));
}

/**
 * Validates that required fields are present in the form data
 */
export function validateRequiredFields(
  values: FormModalData[],
  credentialsSchema: any,
  settingsSchema?: any
): { isValid: boolean; missingFields: string[] } {
  const missingFields: string[] = [];

  // Check credentials schema required fields (excluding const fields)
  if (credentialsSchema?.required) {
    credentialsSchema.required.forEach((fieldName: string) => {
      // Skip const fields as they are automatically handled
      if (credentialsSchema?.properties?.[fieldName]?.const) {
        return;
      }
      
      const field = values.find((f) => (f as any).fieldName === fieldName);
      if (!field || !field.value || field.value.trim() === "") {
        missingFields.push(fieldName);
      }
    });
  }

  // Check settings schema required fields (excluding const fields)
  if (settingsSchema?.required) {
    settingsSchema.required.forEach((fieldName: string) => {
      // Skip const fields as they are automatically handled
      if (settingsSchema?.properties?.[fieldName]?.const) {
        return;
      }
      
      const field = values.find((f) => (f as any).fieldName === fieldName);
      if (!field || !field.value || field.value.trim() === "") {
        missingFields.push(fieldName);
      }
    });
  }

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
}

/**
 * Creates a human-readable error message for missing required fields
 */
export function createMissingFieldsMessage(missingFields: string[]): string {
  if (missingFields.length === 0) return "";
  
  const fieldNames = missingFields.map(field => 
    field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')
  );
  
  if (missingFields.length === 1) {
    return `${fieldNames[0]} is required`;
  }
  
  return `${fieldNames.slice(0, -1).join(', ')} and ${fieldNames.slice(-1)} are required`;
}

/**
 * Extracts field metadata from a schema for debugging/logging purposes
 */
export function extractSchemaMetadata(schema: any): {
  fieldCount: number;
  requiredFields: string[];
  optionalFields: string[];
  constFields: string[];
} {
  const properties = schema?.properties || {};
  const required = schema?.required || [];
  
  const fieldCount = Object.keys(properties).length;
  const requiredFields = required;
  const optionalFields = Object.keys(properties).filter(
    (key) => !required.includes(key)
  );
  const constFields = Object.entries(properties)
    .filter(([_, value]: [string, any]) => value.const)
    .map(([key, _]) => key);

  return {
    fieldCount,
    requiredFields,
    optionalFields,
    constFields,
  };
}
